<template>
    <div class="container">
        <div class="checkout-container" v-if="isOpen && items.length">
            <div class="summary">

                <h4 class="mb-sm fw-bold">Order summary</h4>

                <!-- Список товаров -->
                <div class="products_items" v-if="items.length > 0">
                    <template v-for="(item, i) in items" :key="`${item.product_id}-${item.variation_id}`">
                        <div class="product__item">
                            <div class="product__thumbnail" v-if="item.thumbnail?.thumbnail">
                                <img :src="item.thumbnail.thumbnail" loading="lazy" />
                            </div>
                            <div class="product__thumbnail" v-else>
                                <img :src="`${images_url}/empty/thumbnail_small.png`" loading="lazy" />
                            </div>
                            <div class="product__info">
                                <a :href="products[ item.product_id ].product_url || '/'" class="product__title">
                                    {{ products[ item.product_id ].product_title || '' }}
                                    <div v-if="item.variation_id > 0">
                                        <template v-for="variation in products[ item.product_id ].variations">
                                            <template
                                                v-if="variation.id === item.variation_id && item.properties"
                                            >
                                            <span
                                                class="product__variation"
                                                v-for="prop in item.properties"
                                                :key="prop.id"
                                            >
                                                <span class="product__group">{{ prop.group_name }}:</span>
                                                <span class="product__property_name">{{ prop.property_name }}</span>
                                            </span>
                                            </template>
                                        </template>
                                    </div>
                                </a>
                                <button class="product__delete" @click="removeItem(i)" title="Delete Item">
                                    <i class="icon-trash"></i> Delete
                                </button>
                            </div>
                            <div class="product__prices">
                                <div class="product__sale_price">
                                    {{ NumberFormat.format(item.sale_price / 100) }}
                                </div>
                                <div class="product__price" v-if="!!item?.sale_grant">
                                    {{ NumberFormat.format(item.sale_grant / 100) }}
                                </div>
                                <div class="product__price" v-if="1 === 0 && item.sale_price < item.price">
                                    {{ NumberFormat.format(item.price / 100) }}
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Итоговая сумма -->
                <div class="subtotal d-flex align-center gap-sm justify-between mt-md pt-sm px-sm">
                    <span class="label">Subtotal:</span>
                    <span class="computed fw-bold">
                        {{ NumberFormat.format( sub_total / 100 ) }}
                    </span>
                </div>
                <div class="d-flex text-small bg-light-green fw-bold mt-sm py-sm px-sm radius-md"
                     v-if="grant && ball > 0">
                    <i class="icon-gift"></i> <span>Grant applied</span>
                    <span class="grow"></span>
                    <span class="computed fw-bold">
                        - {{ NumberFormat.format( (sub_total - total) / 100 ) }}
                    </span>
                </div>
                <div class="total d-flex align-center gap-sm justify-between mb-ml mt-sm px-sm">
                    <span class="label fw-bold">Total due today:</span>
                    <span class="computed fw-bold text-extra">
                        {{ NumberFormat.format( total / 100 ) }}
                    </span>
                </div>
            </div>
            <div class="checkout-form">
                <form @submit.prevent="submitForm">
                    <h4 class="mb-sm fw-bold">Contact details</h4>
                    <div class="text-small mb-md">Complete your purchase securely</div>
                    <div class="form-field">
                        <input
                            v-model="user.email"
                            name="email"
                            type="email"
                            id="email"
                            autocomplete="email"
                            placeholder=""
                            :required="!isAuth"
                            @blur="validateEmail"
                            :class="emailClasses"
                            :readonly="isAuth"
                        >
                        <label for="email">Email<sup>*</sup></label>
                    </div>

                    <div class="form-field">
                        <CustomSelect
                            v-model="user.code"
                            :options="codes"
                            placeholder="Select your country code"
                            label="Country code"
                            :require="true"
                            @update:modelValue="updateMask"
                        />
                    </div>
                    <div class="form-field">
                        <input
                            v-model="user.phone_number"
                            name="fn_num_2"
                            type="tel"
                            id="fn_num_2"
                            @input="handleInput"
                            @keydown="handleKeydown"
                            @blur="handleBlur"
                            :placeholder="activeMask"
                            required
                            :class="[emptyField( user.phone_number ), 'on_top']"
                        >
                        <label for="fn_num_2">Your phone number<sup>*</sup></label>
                    </div>

                    <div class="form-field">
                        <input
                            v-model="user.postal_code"
                            name="zip"
                            type="text"
                            id="zip"
                            autocomplete="shipping postal-code"
                            placeholder=""
                            required
                            @change="changeLocation"
                            :class="emptyField( user.postal_code )"
                        >
                        <label for="zip">ZIP<sup>*</sup></label>
                    </div>

                    <button
                        :class="[
                            'btn',
                            'btn--primary',
                            'btn--large',
                            'btn--large-none',
                            'btn--full',
                            'btn--loader',
                        ]"
                        :disabled="isLoading"
                        type="submit"
                    >Proceed to Payment</button>

                    <div class="my-md d-flex align-center justify-center gap-sm">
                        <img :src="`${images_url}/lock.webp`" loading="lazy" style="height: 16px">
                        <span class="caption">Secure payment • Instant delivery</span>
                    </div>

                    <div class="payment-methods">
                        <img alt="Mastercard" :src="`${images_url}/payments/Mastercard.png`" loading="lazy">
                        <img alt="Visa" :src="`${images_url}/payments/Visa.png`" loading="lazy">
                        <img alt="PayPal" :src="`${images_url}/payments/PayPal.png`" loading="lazy">
                        <img alt="Amex" :src="`${images_url}/payments/Amex.png`" loading="lazy">
                        <img alt="Discover" :src="`${images_url}/payments/Discover.png`" loading="lazy">
                        <img alt="Apple Pay" :src="`${images_url}/payments/ApplePay.png`" loading="lazy">
                        <img alt="Google Pay" :src="`${images_url}/payments/GooglePay.png`" loading="lazy">
                    </div>

                    <div class="my-ml d-flex align-center justify-center gap-md">
                        <div class="d-flex gap-sm align-center">
                            <img :src="`${images_url}/secure.svg`" loading="lazy" style="height: 34px">
                            <div class="">
                                <div class="text-small">SSL Secure payment</div>
                                <div class="caption mt-xs">Active SSl 256-bit protection</div>
                            </div>
                        </div>
                        <div class="divider"></div>
                        <div class="d-flex gap-sm align-center">
                            <div class="">
                                <div class="text-small fw-bold d-flex align-center gap-sm">
                                    <span>Excellent</span>
                                    <img :src="`${images_url}/about/rating-group.svg`"
                                         loading="lazy"
                                         class="d-inline-block"
                                         style="height: 16px">
                                </div>
                                <div class="caption d-flex align-center gap-sm mt-xs">
                                    <span>Rated <strong class="text-small">4.6</strong> out of 5 on</span>
                                    <img :src="`${images_url}/about/trust_logo.svg`"
                                         loading="lazy"
                                         class="d-inline-block"
                                         style="height: 16px">
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <div class="empty-cart" v-else-if="items.length === 0 && !isLoading">
            <div class="empty-alert">
                <div class="imw-cart-empty"></div>
                <h4>Your cart is empty</h4>
                <a href="/" class="btn-orange">Back to Shopping</a>
            </div>
        </div>
        <div class="init-page" v-else>
            <!-- Страница ещё грузится -->
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import { toRaw } from 'vue';
import CustomSelect from "@/Goya/components/Common/CustomSelect.vue";

const CART_KEY   = 'cartItems';
const TOTAL_KEY  = 'cartTotal';
const KEY = 'cart'; // Ключ для localStorage
const TTL = 5 * 60 * 1000; // 5 минут для localStorage

export default defineComponent({
    name: "single",
    components: {CustomSelect},
    data() {
        return {
            isOpen: false, // до загрузки страницы показываем скелет или loading
            isLoading: false, // защита от множественных запросов
            images_url: '/templates/Goya/assets/images',
            user: {
                email: '',
                country: 'US',
                postal_code: '',
                phone_number: '',
                code: 'US'
            },
            isEmailValid: false,
            isAuth: false,
            items: [],
            items_original: [],
            products: {},
            inputTimeout: null,
            grant: window.grant || false, // есть ли грант
            ball: window.ball || 0, // сколько на балансе

            countries: window.countries || [],
            stats: window.stats || [],
            NumberFormat: {},

            codes: [],
            activeMask: '(###) ###-####',
            masks: {}
        }
    },
    created() {
        this.NumberFormat = new Intl.NumberFormat('en-US', {style: 'currency', currency: this.stats.currency || 'USD'});
        this.isCartEmpty();
    },
    mounted() {
        this.loadCountries();
        this.init();
    },
    computed: {
        sub_total() {
            return this.items_original.reduce((sum, item) => sum + item.sale_price, 0);
        },
        total() {
            return this.items.reduce((sum, item) => sum + item.sale_price, 0);
        },
        items_count() {
            return this.items.reduce((qty, item) => qty + item.quantity, 0);
        },
        emailClasses() {
            return {
                'filled': this.isEmailValid && this.user.email,
                'error': !this.isEmailValid && this.user.email,
                'empty': ! this.isEmailValid && this.user.email.length === 0,
            };
        }
    },
    watch: {
        // Следим за изменениями массива items
        items: {
            handler() {
                this.isCartEmpty();
            },
            // Важно: отслеживаем изменения содержимого массива
            deep: true
        }
    },
    methods: {

        isCartEmpty() {
            const app = document.getElementById('checkout-app');
            const isEmpty = this.items.length === 0;

            app.classList.toggle('my-auto', isEmpty);
        },

        emptyField( field ) {
            const v = field.trim();
            return v !== '' ? 'filled' : 'empty';
        },

        /**
         * Инициализация страницы.
         * Запрос данных корзины.
         */
        async init() {

            if( this.isLoading )
                return;

            this.isLoading = true

            try {
                const response = await this.$http.post('/cart/get');

                const { status, code, data } = response.data;
                if( data?.items && Array.isArray( data.items ) ) {
                    this.items_original = data.items
                    this.products = data.products || {};
                    this.isAuth = !!data.user;

                    if (this.isAuth && data.user?.email) {
                        this.user.email = data.user.email;
                    }

                    this.prepareItems();
                } else {
                    console.warn('Items not found in response, resetting to empty array');
                    this.items = [];
                    this.products = {};
                }

                this.saveCache(); // Обновляем кэш
            } catch ( err ) {
                console.error('Error loading cart:', err);
            } finally {
                this.isLoading = false;
                this.isOpen = true;
            }
        },

        /**
         * Отправка формы
         */
        async submitForm() {

            if( this.isLoading )
                return;

            this.isLoading = true

            let obj = structuredClone( toRaw( this.user ) );
            obj.phone_number = this.getPhoneNumber();
            obj.country = this.user.code;

            try {
                // Сохранение информации
                const saveResponse = await this.$http.post('/cart/saveInformation', obj)
                const { status: saveStatus, code: saveCode, data: saveData } = saveResponse.data;

                // Проверка результата первого запроса
                if( saveStatus === 'success' && saveData.code === 'ok' ) {
                    // Оформление покупки
                    const purchaseResponse = await this.$http.post('/cart/purchase', {});
                    const { status: purchaseStatus, code: purchaseCode, data: purchaseData } = purchaseResponse.data;

                    // Обработка редиректа
                    if (purchaseData?.redirect) {

                        const data = {
                            products: {},
                            items: [],
                            timestamp: Date.now()
                        };
                        localStorage.setItem( KEY, JSON.stringify(data) );

                        window.location.href = purchaseData.redirect;
                        return;
                    }

                    // Если редиректа нет - показываем ошибку из ответа
                    const errorMessage = purchaseData?.message || 'Unable to complete purchase';
                    this.$alert.error(errorMessage);
                } else {
                    // Ошибка первого запроса
                    const errorMessage = saveData?.message || 'Error saving data';
                    this.$alert.error(errorMessage);
                }

                this.isLoading = false
            } catch (err) {

                console.error('[Cart] Request failed:', err);

                // Сетевая ошибка (не ответ сервера)
                if (err.response) {
                    // Ошибка от сервера (например, 4xx/5xx)
                    const serverMessage = err.response.data?.message || 'The server returned an error';
                    this.$alert.error(serverMessage);
                } else {
                    // Сетевая ошибка (нет соединения и т.п.)
                    this.$alert.error('There is no connection to the server. Check your internet connection and try again.');
                }

                this.isLoading = false
            }
        },

        // Удаляем позицию из корзины
        removeItem( index ) {
            this.updateQuantity( index, 0 )
            setTimeout(() => {
                this.items_original.splice( index, 1 );
            }, 300);

            this.prepareItems();
        },

        // Обновляем количество, удаляем позицию
        updateQuantity( index, qty ) {
            this.items_original[ index ].quantity = qty;
            let item = this.items_original[ index ];

            // Отправляем на сервер данные
            this.set( item )
        },

        prepareItems() {

            if( this.grant ) {
                let ioo = [];
                let amount_wallet = this.ball
                this.items_original.forEach(( i ) => {

                    let ii = structuredClone( toRaw( i ) );

                    ii.sale_grant = ii.sale_price;
                    if( amount_wallet > ii.sale_price ) {
                        amount_wallet -= ii.sale_price;
                        ii.sale_price = 0;
                    } else {
                        ii.sale_price = ii.sale_price - amount_wallet;
                        amount_wallet = 0;
                    }

                    ioo.push( ii )
                })

                this.items = ioo;
            } else {
                this.items = this.items_original;
            }
        },

        /**
         * Запрос к серверу для добавления/обновления/удаления корзины на сервере
         *
         * @param item
         * @returns {Promise<void>}
         */
        async set( item ) {

            if( this.isLoading )
                return;

            this.isLoading = true

            try {
                const response = await this.$http.post('/cart/update', item);
                this.items_original = response.data.data.items;
                this.products = response.data.data.products;

                this.prepareItems();
                this.saveCache(); // обновляем кэш
            } catch ( err ) {
                console.error('Error loading cart:', err);
            } finally {
                this.isLoading = false;
            }
        },

        // Сохранить кэш
        saveCache() {
            const data = {
                products: this.products,
                items: this.items_original,
                timestamp: Date.now()
            };
            localStorage.setItem( KEY, JSON.stringify(data) );
        },

        /**
         * Отправляем изменения о местоположении клиента.
         * Записываем данные об этом так же в storage
         */
        changeLocation() {

        },

        validateEmail() {
            // Регулярка для валидации email (рекомендуемый вариант)
            const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

            if( ! this.user.email ) {
                this.isEmailValid = false;
                this.error = 'Email required';
            } else if ( ! regex.test( this.user.email ) ) {
                this.isEmailValid = false;
                this.error = 'Incorrect email';
            } else {
                this.isEmailValid = true;
                this.error = '';
            }
        },

        /**
         * Грузим страны и коды телефонов
         * @returns {Promise<void>}
         */
        async loadCountries() {
            this.loading = true;
            try {
                const response = await this.$http.post('/ref/countriesList')
                if( response.data.status === 'error' ) {
                    this.error = this.error.message
                    return;
                }

                const countries = response.data.data.countries

                let masks = {};
                let codes = [];

                Object.keys(countries).forEach((iso) => {
                    let country = countries[iso];
                    if( country?.mask && country?.cc && country?.name ) {

                        masks[ iso ] = country.mask.replace(country.cc, '')
                            .replace(/0/g, '#')
                            .trim();

                        codes.push({
                            value: iso,
                            label: `${country.name} ${country.cc}`,
                            code: country.cc,
                        });
                    }
                })

                // Сортируем: сначала страну по умолчанию, потом остальные по алфавиту
                codes.sort((a, b) => {
                    if (a.code === this.user.code) return -1;
                    if (b.code === this.user.code) return 1;
                    return a.label.localeCompare(b.label);
                });

                this.codes = codes;
                this.masks = masks;

            } catch (e) {
                console.error('error: ', e)
            } finally {
                this.loading = false;
            }
        },

        getPhoneNumber() {

            let c = this.masks[ this.user.code ];
            if( c ) {
                const phone = this.user.phone_number.replace(/\D/g, ""); // Только цифры
                if( phone.length ) {

                    c = c.replace(/\D/g, ""); // Только цифры

                    return `+${c}${phone}`;
                }
            }

            return '';
        },

        updateMask( value, isInitial = false ) {

            const mask = this.masks[ this.user.code ]
            if( ! mask || this.activeMask === mask ) return;

            this.activeMask = this.masks[ this.user.code ];

            if( ! isInitial ) {
                this.user.phone_number = '';
            }
        },

        handleKeydown(event) {
            if (event.key === 'Backspace') {
                const input = event.target;
                const start = input.selectionStart;
                const end = input.selectionEnd;

                // Если выделенного текста нет — обрабатываем умное удаление
                if (start === end && start > 0) {
                    const charToDelete = input.value[start - 1];

                    // Если перед курсором не цифра (дефис, скобка, пробел)
                    if (/\D/.test(charToDelete)) {
                        event.preventDefault(); // Останавливаем стандартный Backspace

                        // Ищем позицию последней цифры перед курсором
                        let lastDigitIndex = -1;
                        for (let i = start - 1; i >= 0; i--) {
                            if (/\d/.test(input.value[i])) {
                                lastDigitIndex = i;
                                break;
                            }
                        }

                        if (lastDigitIndex !== -1) {
                            // Удаляем эту цифру из v-model
                            const val = input.value;

                            // Обновляем значение и ставим курсор
                            this.user.phone_number = val.substring(0, lastDigitIndex) + val.substring(start);

                            this.$nextTick(() => {
                                // Ставим курсор туда, где была удалена цифра
                                input.setSelectionRange(lastDigitIndex, lastDigitIndex);
                                // Вызываем handleInput вручную, чтобы маска пересчиталась
                                this.handleInput({ target: input });
                            });
                        }
                    }
                }
            }
        },

        handleInput(event) {
            const input = event.target;
            const oldSelectionStart = input.selectionStart;
            const oldLength = input.value.length;

            const rawValue = input.value.replace(/\D/g, ""); // Только цифры

            if( rawValue.length === 0 ) {
                this.user.phone_number = ''
                return;
            }

            let formatted = "";
            let digitIndex = 0;

            // Проходим по маске и подставляем цифры
            for (let i = 0; i < this.activeMask.length; i++) {
                const maskChar = this.activeMask[i];

                if (maskChar === "#") {
                    // Если в маске решетка — берем следующую цифру из ввода
                    if (digitIndex < rawValue.length) {
                        formatted += rawValue[digitIndex];
                        digitIndex++;
                    } else {
                        break; // Цифры кончились
                    }
                } else {
                    formatted += maskChar;
                    if (digitIndex < rawValue.length && maskChar === rawValue[digitIndex]) {
                        digitIndex++;
                    }
                }
            }

            this.user.phone_number = formatted;

            // Возвращаем курсор на место, иначе он прыгнет в конец
            this.$nextTick(() => {
                // Вычисляем, насколько изменилась длина строки
                const lengthDiff = formatted.length - oldLength;
                let newPosition = oldSelectionStart + lengthDiff;

                // Магия: если курсор уперся в статичный символ маски (пробел, скобку),
                // двигаем его вперед до ближайшей "решетки" или конца строки
                while (
                    newPosition < formatted.length &&
                    this.activeMask[newPosition] !== "#" &&
                    formatted[newPosition] !== rawValue[rawValue.length - 1] // проверка на последнюю введенную цифру
                    ) {
                    newPosition++;
                }

                input.setSelectionRange(newPosition, newPosition);
            });
        },

        handleBlur() {
            const currentVal = this.user.phone_number;
            const currentDigits = currentVal.replace(/\D/g, "");
            const maskDigits = this.activeMask.replace(/[^0-9]/g, "");
            if (currentDigits === maskDigits || currentDigits.length === 0) {
                this.user.phone_number = ""; // Очищаем в ноль
            }
        }
    }
})
</script>

<style scoped>

</style>